<template>
  <v-dialog fullscreen v-model="replicaDialog" max-width="50%">
    <v-card>
      <v-card-title>
        Replicate {{ daDetails.da_no }}

        <v-spacer></v-spacer>

        <v-col cols="6" md="12" lg="3" class="text-right">
          <v-file-input
            ref="replicateDAFileInput"
            outlined
            dense
            hide-details="auto"
            label="Select File"
            v-model="replicateDAFile"
            @change="fileSelected($event)"
          />
        </v-col>
        <v-col cols="12" md="12" lg="2" class="text-right">
          <v-btn depressed color="primary" @click="downloadDAReplicateSample">
            Download Sample
          </v-btn>
        </v-col>

        <v-btn
          icon
          @click="
            replicaDialog = false;
            resetForm();
          "
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-alert dense border="left" type="warning" class="mt-2">
          Container weight must have at most 2 decimals
        </v-alert>

        <v-form v-model="isValid" v-if="replicaDialog">
          <v-data-table
            :headers="headers"
            :items="containerSetList"
            :hide-default-footer="true"
            :disable-pagination="true"
          >
            <template v-slot:item="{ item, index }">
              <tr>
                <td>{{ index + 1 }}</td>
                <td>
                  <v-row no-gutters>
                    <v-col cols="12" md="12" lg="6" class="pr-1">
                      <DateField
                        :inputProps="{
                          dense: true,
                          label: 'Movement Date *',
                          rules: [(val) => !!val || 'Movement Date is Required!'],
                          'hide-details': 'auto',
                        }"
                        :min="twoMonthsAgo"
                        required
                        v-model="rowMovementDates[index]"
                      />
                    </v-col>
                    <v-col cols="12" md="12" lg="6" class="pl-1">
                      <InputField
                        type="time"
                        label="Movement Time *"
                        :rules="[(val) => !!val || 'Movement Time is Required!']"
                        required
                        hide-details="auto"
                        v-model="rowMovementTimes[index]"
                      />
                    </v-col>
                  </v-row>
                </td>
                <td
                  v-for="(containerId, containerIndex) in Object.keys(item)"
                  :key="`${containerId}${containerIndex}`"
                >
                  <v-row>
                    <v-col cols="12" md="12" lg="6" class="text-right">
                      <InputField
                        :rules="[(val) => !!val || 'Container No is Required!']"
                        placeholder="ABCD1234567"
                        required
                        persistent-hint
                        :hint="
                          formFieldHints &&
                          formFieldHints[`${containerId}${index + 1}`]
                        "
                        hide-details="auto"
                        label="Container No"
                        v-model="item[containerId].container_no"
                      />
                    </v-col>

                    <v-col cols="12" md="12" lg="6" class="text-right">
                      <InputField
                        type="number"
                        label="Container Weight (Tons) *"
                        aria-placeholder=""
                        step=".01"
                        :rules="[
                          (v) => !!v || 'Container Weight is Required',
                          (v) =>
                            v > 0 || 'Container Weight can not be less than 0',
                          (v) =>
                            $globals.containerWeightFormatRegExp.test(v) ||
                            'Container Weight must have the format 00.00',
                        ]"
                        hide-details="auto"
                        v-model="item[containerId].weight"
                        @blur="formatContainerWeight(item, containerId)"
                      />
                    </v-col>
                  </v-row>
                </td>
                <td>
                  <v-btn
                    color="primary"
                    small
                    depressed
                    @click="addContainerSet"
                    class="mr-1"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                  <v-btn
                    color="secondary"
                    small
                    v-if="containerSetList.length > 1"
                    depressed
                    @click="removeContainerSet(index)"
                  >
                    <v-icon color="white">mdi-minus</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="d-flex justify-center">
        <v-btn
          depressed
          color="primary"
          large
          :disabled="!isValid"
          @click="addBulkDeliveryAdvice"
        >
          <span>Submit</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import InputField from "@/components/FormBaseComponents/InputField.vue";
import { formatDataKeys } from "@/utils/functions";
import { utils, read, write } from "xlsx";
import FileField from "@/components/FormBaseComponents/FileField.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";

// Accept whatever the user's Excel has (Date object, Excel serial, or a
// string in any common order) and return YYYY-MM-DD, which is what the API
// needs. Returns null when it can't be parsed.
function normalizeSheetDate(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date && !isNaN(value)) {
    return moment(value).format("YYYY-MM-DD");
  }
  if (typeof value === "number") {
    // Excel serial date
    const d = new Date(Math.round((value - 25569) * 86400 * 1000));
    return isNaN(d) ? null : moment(d).format("YYYY-MM-DD");
  }
  const s = String(value).trim();
  const formats = [
    "YYYY-MM-DD",
    "DD-MM-YYYY",
    "MM-DD-YYYY",
    "DD/MM/YYYY",
    "MM/DD/YYYY",
    "YYYY/MM/DD",
    "D-M-YYYY",
    "D/M/YYYY",
    "DD.MM.YYYY",
    "DD MMM YYYY",
    "DD MMMM YYYY",
    moment.ISO_8601,
  ];
  const m = moment(s, formats, true);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
}

export default {
  components: { InputField, FileField, DateField },
  props: {
    comeFromCreation: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Boolean,
    },
    daDetails: {
      type: Object,
      required: true,
    },
    daPayload: {
      type: Object,
      required: false,
    },
    replicateNewDA: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  watch: {
    replicaDialog(val) {
      if (val) {
        this.setHeaders();
        if (this.comeFromCreation && this.daPayload) {
          this.daDetails.movement_date = this.daPayload.movement_date;
          this.daDetails.movement_time = this.daPayload.movement_time;
        } else {
          this.daDetails.movement_date = this.today;
        }
        this.addContainerSet();
      }
    },
    containerSetList: {
      handler(val) {
        if (val && val.length > 0 && !this.replicateDAFile) {
          val.forEach((item, index) => {
            Object.keys(item).forEach((containerId, containerIndex) => {
              let formFieldHintsKey = `${containerId}${index + 1}`;
              if (item[containerId] && item[containerId].container_no) {
                let container_no = item[containerId].container_no;
                if (
                  container_no &&
                  !this.$globals.containerNumberRegExp.test(container_no)
                ) {
                  this.formFieldHints[formFieldHintsKey] =
                    "Invalid Container No!";
                } else {
                  delete this.formFieldHints[formFieldHintsKey];
                }
              }
            });
          });
        }
      },
      deep: true,
    },
  },
  data() {
    return {
      formFieldHints: {},
      movement_date: null,

      isValid: true,
      replicateDAFile: null,
      containerSetList: [],
      rawData: [],
      rowMovementDates: {},
      rowMovementTimes: {},
      headers: [
        { text: "#", value: "index", sortable: false },
        { text: "Movement Date & Time", align: "center", value: "movement_date", sortable: false },
        { text: "Actions", align: "center", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    today() {
      return new Date().toISOString().slice(0, 10);
    },
    nowTime() {
      const d = new Date();
      const hours = `${d.getHours()}`.padStart(2, "0");
      const minutes = `${d.getMinutes()}`.padStart(2, "0");
      return `${hours}:${minutes}`;
    },
    twoMonthsAgo() {
      const d = new Date();
      d.setMonth(d.getMonth() - 2);
      return d.toISOString().slice(0, 10);
    },
    replicaDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    daContainerSet() {
      return this.daDetails.delivery_advice_containers;
    },
    cols() {
      return this.daContainerSet.length > 0
        ? 12 / this.daContainerSet.length
        : 12;
    },
  },

  methods: {
    downloadDAReplicateSample() {
      const workBookName = `REPLICATE_DA_SAMPLE_${new Date()
        .toISOString()
        .slice(0, 10)}`;

      var wb = utils.book_new();
      wb.Props = {
        Title: workBookName,
        CreatedDate: new Date(),
      };

      const containerCount = this.daContainerSet.length || 1;
      const headers = ["Movement Date", "Movement Time"];
      const sampleRow = [
        this.daDetails.movement_date || this.today,
        this.daDetails.movement_time || this.nowTime,
      ];
      for (let i = 0; i < containerCount; i++) {
        headers.push("Container No", "Container Weight");
        sampleRow.push(`CONT${i + 1}000`, "12.00");
      }

      const ws = utils.json_to_sheet([]);
      utils.sheet_add_aoa(ws, [headers, sampleRow]);

      utils.book_append_sheet(wb, ws, "Sheet1");

      const wbout = write(wb, { bookType: "xlsx", type: "binary" });
      const blob = new Blob([this.s2ab(wbout)], {
        type: "application/octet-stream",
      });
      this.download(blob, workBookName);

      this.$bus.$emit("showLoader", false);
    },
    download(blob, workBookName) {
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      const fileName = ` ${workBookName}.xlsx`;

      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    s2ab(s) {
      var buf = new ArrayBuffer(s.length);
      var view = new Uint8Array(buf);
      for (var i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
      return buf;
    },
    fileSelected(file) {
      if (file) {
        const name = file.name;
        const lastDot = name.lastIndexOf(".");
        const ext = name.substring(lastDot + 1);
        let allowed = /(\xlsx|\xls)$/i;
        if (!allowed.exec(ext)) {
          this.$nextTick(() => {
            this.$refs.replicateDAFileInput.reset();
            this.$bus.$emit("showToastMessage", {
              message: "This file format is not supported!",
              color: "error",
            });
          });
          return false;
        }
      }

      if (file && file.size > 2e6) {
        this.$bus.$emit("showToastMessage", {
          message: "File size must be less than 2 MB",
          color: "error",
        });
        return;
      }
      if (file) {
        this.$bus.$emit("showLoader", true);
        let reader = new FileReader();
        reader.onload = async () => {
          let fileData = reader.result;

          let wb = read(fileData, {
            type: "binary",
            cellDates: true,
          });
          const sheetData = utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
            raw: false,
            dateNF: "yyyy-mm-dd",
          });
          if (sheetData && sheetData.length == 0) {
            this.$bus.$emit("showToastMessage", {
              message: "Empty Sheet",
              color: "error",
            });
            this.$bus.$emit("showLoader", false);

            return false;
          }

          const rawData = [
            ...(await formatDataKeys(sheetData, " ", "_", "lowercase")),
          ];
          if (rawData.length > 500) {
            this.$bus.$emit("showLoader", false);
            this.$bus.$emit("showToastMessage", {
              message: "Cannot add more than 500 replica at a time",
              color: "error",
            });
            this.$refs.replicateDAFileInput.reset();
            return;
          }
          try {
            let _distinctContainerIds = [];
            if (this.containerSetList.length) {
              _distinctContainerIds = Object.keys(this.containerSetList[0]);
            }

            this.rowMovementDates = {};
            this.rowMovementTimes = {};
            this.containerSetList = rawData.map((containerSet, rowIndex) => {
              const obj = {};
              let index = 0;
              while (Object.keys(obj).length != _distinctContainerIds.length) {
                obj[_distinctContainerIds[index]] = {};

                obj[_distinctContainerIds[index]]["container_no"] =
                  containerSet[`container_no${!!index ? "_" + index : ""}`];

                const uploadedWeight =
                  containerSet[`container_weight${!!index ? "_" + index : ""}`];
                const numericWeight = Number(uploadedWeight);
                obj[_distinctContainerIds[index]]["weight"] =
                  uploadedWeight && !isNaN(numericWeight)
                    ? numericWeight.toFixed(2)
                    : "12.00";

                index++;
              }
              this.$set(
                this.rowMovementDates,
                rowIndex,
                normalizeSheetDate(containerSet["movement_date"]) ||
                  this.daDetails.movement_date
              );
              this.$set(
                this.rowMovementTimes,
                rowIndex,
                containerSet["movement_time"] ||
                  this.daDetails.movement_time ||
                  this.nowTime
              );
              return obj;
            });
            this.$bus.$emit("showLoader", false);
          } catch (err) {
            this.$bus.$emit("showLoader", false);
            this.$bus.$emit("showToastMessage", {
              message: "Some issue with the excel sheet data",
              color: "error",
            });
            this.replicateDAFile = null;
          }
        };

        reader.readAsBinaryString(file);
      } else {
        this.errors = [];
        this.rawData = [];
      }
    },
    setHeaders() {
      this.daContainerSet.forEach((container) => {
        this.headers.splice(this.headers.length - 1, 0, {
          text: container.container_type_title,
          align: "center",
          value: container.id,
          sortable: false,
        });
      });
    },
    formatContainerWeight(item, containerId) {
      const value = item[containerId].weight;
      if (value === "" || value === null || value === undefined) {
        return;
      }
      const numeric = Number(value);
      if (!isNaN(numeric)) {
        item[containerId].weight = numeric.toFixed(2);
      }
    },
    addContainerSet() {
      if (this.containerSetList.length != 500) {
        let obj = {};
        this.daContainerSet.forEach((c, index) => {
          obj[c.id] = { weight: "12.00" };
          if (this.daContainerSet.length == index + 1) {
            let formFieldHintsKey = `${c.id}${this.containerSetList.length}`;
            let newIndex = this.containerSetList.length;
            this.containerSetList.push(obj);
            this.formFieldHints[formFieldHintsKey] = "";
            this.$set(
              this.rowMovementDates,
              newIndex,
              this.daDetails.movement_date
            );
            this.$set(
              this.rowMovementTimes,
              newIndex,
              this.daDetails.movement_time || this.nowTime
            );
          }
        });
      } else {
        this.$bus.$emit("showToastMessage", {
          message: "Cannot add more than 500 replica at a time",
          color: "error",
        });
      }
    },
    removeContainerSet(index) {
      if (this.containerSetList.length > 1) {
        this.containerSetList.splice(index, 1);

        const reindexedDates = {};
        const reindexedTimes = {};
        this.containerSetList.forEach((_, newIndex) => {
          const oldIndex = newIndex >= index ? newIndex + 1 : newIndex;
          reindexedDates[newIndex] = this.rowMovementDates[oldIndex];
          reindexedTimes[newIndex] = this.rowMovementTimes[oldIndex];
        });
        this.rowMovementDates = reindexedDates;
        this.rowMovementTimes = reindexedTimes;
      }
    },
    async formatData() {
      const formatted = this.containerSetList.map((containerSet, index) => {
        const movementDate =
          this.rowMovementDates[index] || this.daDetails.movement_date;
        const movementTime =
          this.rowMovementTimes[index] ||
          this.daDetails.movement_time ||
          this.nowTime;
        return Object.keys(containerSet).map((containerId) => {
          return {
            ...containerSet[containerId],
            order_container_id: containerId,
            movement_date: movementDate,
            movement_time: movementTime,
          };
        });
      });

      return formatted;
    },

    async addBulkDeliveryAdvice() {
      const formattedData = await this.formatData();

      if (this.replicateNewDA) {
        const payload = {
          ...this.daPayload,
          replicate_da: formattedData,
        };

        this.$emit("replicateDA", payload);
        this.resetForm();
        return;
      }

      const data = {
        id: this.daDetails.id,
        data: formattedData,
      };

      this.$bus.$emit("showLoader", true);

      this.$api.deliveryAdvice
        .addBulkDeliveryAdvice(data)
        .then((res) => {
          this.$emit("refreshList");
          this.replicaDialog = false;
          this.resetForm();

          this.$bus.$emit("showToastMessage", {
            message: "Delivery Advices created successfully",
            color: "success",
          });

          this.$router.push("/app/admin/trip/new-list");
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          console.error("API Error:", err);

          this.$bus.$emit("showToastMessage", {
            message:
              err?.data?.non_field_errors?.join() || "Cannot replicate DA",
            color: "error",
          });
        });
    },

    resetForm() {
      this.formFieldHints = {};
      this.containerSetList = [];
      this.rowMovementDates = {};
      this.rowMovementTimes = {};
      this.headers.splice(2, this.headers.length - 3);
      this.$refs.replicateDAFileInput.reset();
    },
  },
};
</script>
